import { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classes from './Notifications.module.css';
import { updateUserProfile } from '../../store/authSlice';

const Notifications = () => {
    const dispatch = useDispatch();
    const notificationPreferencesFromStore = useSelector(
        (state) => state?.auth?.user?.notificationPreferences
    );

    const DEFAULT_PREFERENCES = useMemo(() => ({
        accountActivitiesEmail: true,
        accountActivitiesPush: true,
        accountActivitiesSms: false,
        recommendationsEmail: true,
        recommendationsPush: false,
        offersUpdatesEmail: true,
        offersUpdatesPush: false,
    }), []);

    const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const isInitialMount = useRef(true);
    const saveTimeoutRef = useRef(null);

    useEffect(() => {
        // Only sync from Redux store on initial mount to avoid fighting with local edits
        if (isInitialMount.current) {
            if (notificationPreferencesFromStore) {
                setPreferences({
                    ...DEFAULT_PREFERENCES,
                    ...notificationPreferencesFromStore,
                    accountActivitiesEmail: true,
                });
            } else {
                setPreferences(DEFAULT_PREFERENCES);
            }
            isInitialMount.current = false;
        }
    }, [notificationPreferencesFromStore, DEFAULT_PREFERENCES]);

    const pushEnabled = useMemo(
        () =>
            preferences.accountActivitiesPush ||
            preferences.recommendationsPush ||
            preferences.offersUpdatesPush,
        [preferences]
    );

    const handleToggle = (category, type) => {
        setPreferences((prev) => {
            let next;

            switch (category) {
                case 'importantAccount':
                    next = {
                        ...prev,
                        accountActivitiesPush:
                            type === 'push' ? !prev.accountActivitiesPush : prev.accountActivitiesPush,
                        accountActivitiesSms:
                            type === 'sms' ? !prev.accountActivitiesSms : prev.accountActivitiesSms,
                    };
                    break;
                case 'recommendations':
                    next = {
                        ...prev,
                        recommendationsEmail:
                            type === 'email' ? !prev.recommendationsEmail : prev.recommendationsEmail,
                        recommendationsPush:
                            type === 'push' ? !prev.recommendationsPush : prev.recommendationsPush,
                    };
                    break;
                case 'offersAndUpdates':
                    next = {
                        ...prev,
                        offersUpdatesEmail:
                            type === 'email' ? !prev.offersUpdatesEmail : prev.offersUpdatesEmail,
                        offersUpdatesPush:
                            type === 'push' ? !prev.offersUpdatesPush : prev.offersUpdatesPush,
                    };
                    break;
                default:
                    next = prev;
            }

            return next;
        });
    };

    const handleSave = async (prefsToSave = null) => {
        try {
            setIsSaving(true);
            setSaveSuccess(false);

            // Use provided preferences or current state
            const prefs = prefsToSave || preferences;

            // Strip GraphQL metadata like __typename before sending to backend
            // to avoid sending unsupported fields in NotificationPreferencesInput
            const { __typename, ...cleanPreferences } = prefs || {};

            const payload = {
                notificationPreferences: {
                    ...cleanPreferences,
                    accountActivitiesEmail: true,
                },
            };
            
            await dispatch(updateUserProfile(payload)).unwrap();
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 2500);

        } catch (error) {
            console.error('Error saving notification preferences:', error);
        } finally {
            setIsSaving(false);
        }
    };

    // Auto-save when preferences change (debounced)
    useEffect(() => {
        // Skip auto-save on initial mount
        if (isInitialMount.current) {
            return;
        }

        // Clear existing timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Debounce: wait 500ms after last change before saving
        saveTimeoutRef.current = setTimeout(() => {
            handleSave(preferences);
        }, 500);

        // Cleanup timeout on unmount
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [preferences]);

    const ToggleSwitch = ({ checked, onChange, disabled = false }) => {
        return (
            <div className={classes.switchContainer}>
                <label className={`${classes.switch} ${disabled ? classes.switchDisabled : ''}`}>
                    <input 
                        type="checkbox" 
                        checked={checked}
                        onChange={onChange}
                        disabled={disabled}
                    />
                    <span className={classes.slider}>
                        <span className={classes.checkmark}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="11" viewBox="0 0 15 11" fill="none">
                                <line x1="0.535943" y1="5.71655" x2="4.75815" y2="10.0296" stroke="black" strokeWidth="1.5"/>
                                <line x1="3.70576" y1="10.0117" x2="13.6601" y2="0.543604" stroke="black" strokeWidth="1.5"/>
                            </svg>
                        </span>
                    </span>
                </label>
            </div>
        );
    };

    return (
        <div className={classes.container}>
            <h1 className="titleSemibold2">Benachrichtigungen</h1>
            
            {!pushEnabled && (
                <div className={classes.warningBanner}>
                    <div className={classes.warningContent}>
                        <div className={classes.warningIcon}>
                            !
                        </div>
                        <span className="regularXs">Deine Push-Nachrichten <br/> sind deaktiviert</span>
                    </div>
                    <button 
                        className={classes.activateButton}
                        onClick={() => handleToggle('importantAccount', 'push')}
                    >
                        Aktivieren
                    </button>
                </div>
            )}

            <div className={classes.section}>
                <div className={classes.sectionHeader}>
                    <h2 className="semiboldLarge">Wichtige Konto-Aktivitäten</h2>
                    <p className="regularXs">
                    Buchungsanfragen, Nachrichten, <br/> Buchungserinnerungen, Bewertungsanfragen und <br/> wichtige Informationen und Updates zu deinem Konto.                    
                    </p>
                </div>
                <div className={classes.optionsContainer}>
                    <div className={classes.optionSeparator}></div>
                    <div className={classes.option}>
                        <span className="regularMiddle">E-Mail</span>
                        <ToggleSwitch 
                            checked={true}
                            onChange={() => {}}
                            disabled={true}
                        />
                    </div>
                    <div className={classes.optionSeparator}></div>
                    <div className={classes.option}>
                        <div className={classes.optionLabel}>
                            <span className="regularMiddle">Push-Benachrichtigungen</span>
                            <span className={classes.subLabel}>Handy oder Tablet</span>
                        </div>
                        <ToggleSwitch 
                            checked={preferences.accountActivitiesPush}
                            onChange={() => handleToggle('importantAccount', 'push')}
                        />
                    </div>
                    <div className={classes.optionSeparator}></div>
                    <div className={classes.option}>
                        <span className="regularMiddle">SMS</span>
                        <ToggleSwitch 
                            checked={preferences.accountActivitiesSms}
                            onChange={() => handleToggle('importantAccount', 'sms')}
                        />
                    </div>
                </div>
            </div>

            <div className={classes.section}>
                <div className={classes.sectionHeader}>
                    <h2 className="semiboldLarge">Empfehlungen</h2>
                    <p className="regularXs">
                        Empfehlungen für Studios, die dir gefallen.
                    </p>
                </div>
                <div className={classes.optionsContainer}>
                    <div className={classes.optionSeparator}></div>
                    <div className={classes.option}>
                        <span className="regularMiddle">E-Mail</span>
                        <ToggleSwitch 
                            checked={preferences.recommendationsEmail}
                            onChange={() => handleToggle('recommendations', 'email')}
                        />
                    </div>
                    <div className={classes.optionSeparator}></div>
                    <div className={classes.option}>
                        <span className="regularMiddle">Push-Benachrichtigungen</span>
                        <ToggleSwitch 
                            checked={preferences.recommendationsPush}
                            onChange={() => handleToggle('recommendations', 'push')}
                        />
                    </div>
                </div>
            </div>

            <div className={classes.section}>
                <div className={classes.sectionHeader}>
                    <h2 className="semiboldLarge">Angebote und Updates</h2>
                    <p className="regularXs">
                        Updates, Neuigkeiten und Angebote.
                    </p>
                </div>
                <div className={classes.optionsContainer}>
                    <div className={classes.optionSeparator}></div>
                    <div className={classes.option}>
                        <span className="regularMiddle">E-Mail</span>
                        <ToggleSwitch 
                            checked={preferences.offersUpdatesEmail}
                            onChange={() => handleToggle('offersAndUpdates', 'email')}
                        />
                    </div>
                    <div className={classes.optionSeparator}></div>
                    <div className={classes.option}>
                        <span className="regularMiddle">Push-Benachrichtigungen</span>
                        <ToggleSwitch 
                            checked={preferences.offersUpdatesPush}
                            onChange={() => handleToggle('offersAndUpdates', 'push')}
                        />
                    </div>
                </div>
            </div>
            
        </div>
    );
};

export default Notifications;

